"use client"

import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Search, Mail, Calendar, MessageSquare } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { Email } from "openapi/api/model"
import { useGetEmails } from "openapi/api/endpoints"

type PersonTouchpointsProps = {
    personId: string
    touchPoints?: Email[]
    isLoading?: boolean
    isError?: boolean
    onRetry?: () => void
}

export function PersonTouchpoints({
    personId,
    touchPoints: providedTouchPoints,
    isLoading: providedIsLoading,
    isError: providedIsError,
    onRetry,
}: PersonTouchpointsProps) {
    const [searchQuery, setSearchQuery] = useState("")
    const hasProvidedQueryState =
        providedTouchPoints !== undefined || providedIsLoading !== undefined || providedIsError !== undefined
    const {
        data: fetchedTouchPoints,
        isLoading: isLoadingFetchedTouchPoints,
        isError: hasFetchedTouchPointsError,
        refetch,
    } = useGetEmails({ contact_id: personId }, { query: { enabled: !hasProvidedQueryState } })
    const touchPoints = hasProvidedQueryState ? providedTouchPoints : fetchedTouchPoints
    const isLoading = providedIsLoading ?? isLoadingFetchedTouchPoints
    const isError = providedIsError ?? hasFetchedTouchPointsError
    const [selectedTouchPoint, setSelectedTouchPoint] = useState<Email | null>(null)

    const retryTouchPoints = onRetry ?? (() => {
        void refetch()
    })

    const filteredTouchPoints = (touchPoints ?? []).filter((tp) => {
        const query = searchQuery.toLowerCase()
        return (
            tp.subject.toLowerCase().includes(query) ||
            tp.body.toLowerCase().includes(query)
        )
    })

    const getTypeIcon = (type: string) => {
        switch (type) {
            case "email":
                return <Mail className="h-4 w-4" />
            case "call":
            case "meeting":
                return <Calendar className="h-4 w-4" />
            case "message":
                return <MessageSquare className="h-4 w-4" />
            default:
                return <Mail className="h-4 w-4" />
        }
    }

    if (isLoading) {
        return <div className="py-8 text-center text-sm text-muted-foreground">Loading touchpoints...</div>
    }

    if (isError) {
        return (
            <div className="flex flex-col gap-3 py-8 text-center text-sm text-muted-foreground">
                <span>Could not load touchpoints.</span>
                <Button type="button" variant="outline" size="sm" className="mx-auto" onClick={retryTouchPoints}>
                    Retry
                </Button>
            </div>
        )
    }

    return (
        <>
            <div className="space-y-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search touchpoints by subject, content, or type..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                    />
                </div>

                {filteredTouchPoints.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>{searchQuery ? "No touchpoints match your search." : "No touchpoints recorded yet."}</p>
                    </div>
                ) : (
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Subject</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Content Preview</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredTouchPoints?.map((touchPoint) => (
                                    <TableRow
                                        key={touchPoint.id}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => setSelectedTouchPoint(touchPoint)}
                                    >
                                        <TableCell>
                                            <div className="flex items-center space-x-2">
                                                {getTypeIcon('email')}
                                                <span className="capitalize">{'email'}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-medium">{touchPoint.subject}</TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {new Date(touchPoint.sent_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell className="max-w-xs">
                                            <p className="truncate text-sm text-muted-foreground">{touchPoint.body}</p>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* Dialog to show full touchpoint message */}
            <DialogShell
                open={!!selectedTouchPoint}
                onOpenChange={() => setSelectedTouchPoint(null)}
                wide
                icon={selectedTouchPoint ? getTypeIcon("email") : undefined}
                title={selectedTouchPoint?.subject ?? "Touchpoint"}
            >
                <p className="flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
                    <span>Email</span>
                    <span>
                        {selectedTouchPoint &&
                            new Date(selectedTouchPoint.sent_at).toLocaleString()}
                    </span>
                </p>
                <p className="mt-3.5 whitespace-pre-wrap rounded-[8px] border bg-muted/30 p-3.5 text-[12.5px] leading-[1.7]">
                    {selectedTouchPoint?.body}
                </p>
            </DialogShell>
        </>
    )
}
